import React, { useState } from "react";

import { Button, Card } from "react-bootstrap";

import classNames from "classnames";
import { FieldValues, UseFormReset } from "react-hook-form";

import { ContactUsInput, useContactUsMutation } from "@api/graphql";
import ContactUsPlaceholder from "@assets/contact_us_placeholder.jpg";
import { ToastMessage } from "@components/ToastMessage";
import TextArea from "@components/component-library/Atoms/TextArea/TextArea";
import Texfield from "@components/component-library/Atoms/TextField/Texfield";
import Form from "@components/component-library/Molecules/Form/Form";
import PublicPagesWrapper from "@components/component-library/Wrappers/PublicPagesWrapper";
import { EMAIL, INTERNATIONAL_PHONE } from "@util/regex";

import styles from "./ContactUs.module.scss";

const ContactUs = () => {
  const [contactUs] = useContactUsMutation({});

  const [toastMessageData, setToastMessageData] = useState<{
    message: string;
    variant?: "error" | "success" | "info";
  }>();

  const [showToast, setShowToast] = useState(false);

  return (
    <>
      <PublicPagesWrapper>
        <h1 className={styles["heading"]}>Contact us</h1>
        <div className={styles["content-container"]}>
          <div className={styles["left-content-container"]}>
            <div className="monoBlack b3">
              Get in touch with our team and we will get back to you as soon as we can. You can also call us toll free
              to discuss your query during office hours on <a href="tel:1800066844">1800 066 844</a>.
            </div>
            <div className={styles["card-image"]}>
              <Card.Img srcSet={ContactUsPlaceholder} />
              <caption className="align-self-end text-end monoGrey1 b5 medium py-0">image credit: AlexD75</caption>
            </div>
          </div>
          <div className={styles["right-content-container"]}>
            <ToastMessage
              message={toastMessageData?.message || ""}
              show={showToast}
              setShow={setShowToast}
              type={toastMessageData?.variant}
            />
            <Form
              onSubmit={async (data: FieldValues, reset: UseFormReset<FieldValues>) => {
                const res = await contactUs({
                  variables: {
                    input: {
                      ...data,
                      middle_name: data.middle_name || "",
                    } as ContactUsInput,
                  },
                });
                if (res.data?.contactUs?.success) {
                  setToastMessageData({
                    message: "Enquiry has been sent. Our team will contact you Shortly",
                    variant: "success",
                  });
                  setShowToast(true);
                  reset({
                    given_name: "",
                    middle_name: "",
                    last_name: "",
                    phone: "",
                    email: "",
                    message: "",
                  });
                } else {
                  setToastMessageData({
                    message: "Error sending enquiry. Please try again later.",
                    variant: "error",
                  });
                  setShowToast(true);
                }
              }}
            >
              <div className={classNames(styles["span-two-cols-in-grid"], "monoGrey2 b4")}>* Mandatory field</div>
              <Texfield
                label="* Given name"
                name="given_name"
                placeholder="Enter your given name"
                rules={{ required: "Given name required" }}
              />
              <Texfield
                label="* Middle name"
                name="middle_name"
                type="hidden"
                placeholder="Enter your middle name"
                hidden
              />
              <Texfield
                label="* Last name"
                name="last_name"
                placeholder="Enter your last name"
                rules={{ required: "Last name required" }}
              />
              <Texfield
                label="* Phone"
                name="phone"
                type="number"
                placeholder="Enter your mobile number"
                rules={{
                  required: "Mobile number required",
                  pattern: {
                    value: INTERNATIONAL_PHONE,
                    message: "Invalid mobile number. Must be 10 digits.",
                  },
                }}
                onWheel={(event) => event.currentTarget.blur()}
              />
              <Texfield
                label="* Email address"
                name="email"
                placeholder="Enter your email address"
                rules={{
                  required: "Email address required",
                  pattern: {
                    value: EMAIL,
                    message: "Invalid email address.",
                  },
                }}
              />
              <TextArea
                label="* Message"
                name="message"
                placeholder="Enter your message here"
                rules={{
                  required: "Message required",
                }}
                rows={5}
              />
              <div className={classNames(styles["span-two-cols-in-grid"], "d-flex justify-content-end")}>
                <Button type="submit">Submit</Button>
              </div>
            </Form>
          </div>
        </div>
      </PublicPagesWrapper>
    </>
  );
};

export default ContactUs;
